import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, X, Power, KeyRound, Eye, EyeOff, Coins, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function SalespersonsPage() {
  const [salespersons, setSalespersons] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [creditTarget, setCreditTarget] = useState(null);
  const { refresh } = useAuth();

  const load = () => {
    api.get("/salespersons").then((r) => setSalespersons(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (person) => {
    try {
      await api.patch(`/salespersons/${person.id}`, null, { params: { is_active: !person.is_active } });
      toast.success(person.is_active ? "Salesperson deactivated" : "Salesperson activated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="space-y-8" data-testid="salespersons-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="overline">Team</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">Sales Team</h1>
          <p className="mt-2 text-[#5C5F62]">Each salesperson gets their own login and personal visualizer.</p>
        </div>
        <button
          data-testid="add-salesperson-btn"
          onClick={() => setShowAdd(true)}
          className="neivai-btn-primary inline-flex items-center gap-2 self-start md:self-auto"
        >
          <Plus className="h-4 w-4" /> Add Salesperson
        </button>
      </div>

      {salespersons.length === 0 ? (
        <div className="neivai-card p-12 text-center">
          <div className="flex justify-center mb-4">
            <span className="h-14 w-14 rounded-full bg-[#F3F1EC] flex items-center justify-center">
              <Users className="h-6 w-6 text-[#C86B53]" />
            </span>
          </div>
          <p className="font-medium text-[#1A1C1E]">No salespeople yet</p>
          <p className="text-sm text-[#8A8D91] mt-1">Click &ldquo;Add Salesperson&rdquo; to create the first login.</p>
          <button onClick={() => setShowAdd(true)} className="neivai-btn-primary inline-flex items-center gap-2 mt-6">
            <Plus className="h-4 w-4" /> Add Salesperson
          </button>
        </div>
      ) : (
        <>
          {/* Card view for small screens */}
          <div className="grid gap-4 sm:hidden">
            {salespersons.map((p) => (
              <div key={p.id} className="neivai-card p-4 space-y-3" data-testid={`salesperson-row-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-[#1A1C1E]">{p.name}</p>
                    <p className="text-sm text-[#5C5F62] break-all">{p.email}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setCreditTarget(p)} title="Manage Credits" className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                      <Coins className="h-3.5 w-3.5 text-[#C86B53]" />
                    </button>
                    <button onClick={() => setResetTarget(p)} title="Reset Password" className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                      <KeyRound className="h-3.5 w-3.5 text-[#C86B53]" />
                    </button>
                    <button data-testid={`toggle-salesperson-${p.id}`} onClick={() => toggleActive(p)} title={p.is_active ? "Deactivate" : "Activate"} className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                      <Power className={`h-3.5 w-3.5 ${p.is_active ? "text-[#2E7D46]" : "text-[#8A8D91]"}`} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_verified ? "bg-[#E7F3EA] text-[#2E7D46]" : "bg-[#FEF3F2] text-[#B74F4F]"}`}>
                    {p.is_verified ? "✓ Verified" : "Pending"}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? "bg-[#E7F3EA] text-[#2E7D46]" : "bg-[#F3F1EC] text-[#8A8D91]"}`}>
                    {p.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5F62]">Credits: <span className="font-medium text-[#1A1C1E]">{p.credits ?? 0}</span></span>
                  <span className="text-[#8A8D91]">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Table view for larger screens */}
          <div className="neivai-card overflow-x-auto no-scrollbar overscroll-x-contain hidden sm:block" style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}>
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E5] text-left text-[#8A8D91]">
                  <th className="p-4 font-medium">Name</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Verified</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Credits</th>
                  <th className="p-4 font-medium">Created</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {salespersons.map((p) => (
                  <tr key={p.id} className="border-b border-[#E5E5E5] last:border-0" data-testid={`salesperson-row-${p.id}`}>
                    <td className="p-4 font-medium">{p.name}</td>
                    <td className="p-4 text-[#5C5F62]">{p.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${p.is_verified ? "bg-[#E7F3EA] text-[#2E7D46]" : "bg-[#FEF3F2] text-[#B74F4F]"}`}>
                        {p.is_verified ? "✓ Verified" : "Pending"}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? "bg-[#E7F3EA] text-[#2E7D46]" : "bg-[#F3F1EC] text-[#8A8D91]"}`}>
                        {p.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-4 font-medium">{p.credits ?? 0}</td>
                    <td className="p-4 text-[#8A8D91] whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="p-4">
                      <div className="flex justify-end items-center gap-1">
                        <button onClick={() => setCreditTarget(p)} title="Manage Credits" className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                          <Coins className="h-3.5 w-3.5 text-[#C86B53]" />
                        </button>
                        <button onClick={() => setResetTarget(p)} title="Reset Password" className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                          <KeyRound className="h-3.5 w-3.5 text-[#C86B53]" />
                        </button>
                        <button data-testid={`toggle-salesperson-${p.id}`} onClick={() => toggleActive(p)} title={p.is_active ? "Deactivate" : "Activate"} className="neivai-btn-secondary p-2 inline-flex items-center justify-center">
                          <Power className={`h-3.5 w-3.5 ${p.is_active ? "text-[#2E7D46]" : "text-[#8A8D91]"}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAdd && <AddSalespersonDialog onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {resetTarget && <ResetPasswordDialog person={resetTarget} onClose={() => setResetTarget(null)} />}
      {creditTarget && <AddCreditsDialog person={creditTarget} onClose={() => setCreditTarget(null)} onSaved={() => { setCreditTarget(null); load(); refresh(); }} />}
    </div>
  );
}

function AddSalespersonDialog({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [created, setCreated] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email)) newErrors.email = "Invalid email format";
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6) newErrors.password = "Password must be at least 6 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/salespersons", { name, email, password });
      toast.success("Salesperson created. Verification email sent.");
      setCreated(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.msg) {
        toast.error(detail.msg);
      } else {
        toast.error("Failed to save");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C1E]/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden">
        {created ? (
          <>
            <div className="p-6 border-b border-[#E5E5E5]">
              <h3 className="text-2xl font-display font-medium">Verification email sent</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#E7F3EA] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-5 w-5 text-[#2E7D46]" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1C1E]">Salesperson created</p>
                  <p className="text-sm text-[#8A8D91] mt-1">A verification email has been sent to <strong>{email}</strong></p>
                </div>
              </div>
              <div className="bg-[#F3F1EC] rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-[#1A1C1E]">Next steps:</p>
                <ol className="text-sm text-[#5C5F62] space-y-1 list-decimal list-inside">
                  <li>Salesperson opens the verification email</li>
                  <li>Clicks the verification link</li>
                  <li>Can then log in and start visualizing</li>
                </ol>
              </div>
              <p className="text-xs text-[#8A8D91]">The verification link expires in 24 hours.</p>
            </div>
            <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
              <button onClick={() => { setCreated(false); onSaved(); }} className="neivai-btn-primary">Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="text-2xl font-display font-medium">Add salesperson</h3>
              <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <input
                  data-testid="salesperson-name-input"
                  value={name}
                  onChange={(e) => { setName(e.target.value); if (errors.name) setErrors({ ...errors, name: "" }); }}
                  placeholder="Full name"
                  className={`w-full bg-white border rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] ${errors.name ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                />
                {errors.name && <p className="text-xs text-[#B74F4F] mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  data-testid="salesperson-email-input"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({ ...errors, email: "" }); }}
                  type="email"
                  placeholder="Email"
                  className={`w-full bg-white border rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] ${errors.email ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                />
                {errors.email && <p className="text-xs text-[#B74F4F] mt-1">{errors.email}</p>}
              </div>
              <div>
                <input
                  data-testid="salesperson-password-input"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors({ ...errors, password: "" }); }}
                  type="password"
                  placeholder="Password (min 6 characters)"
                  className={`w-full bg-white border rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] ${errors.password ? "border-[#B74F4F]" : "border-[#E5E5E5]"}`}
                />
                {errors.password && <p className="text-xs text-[#B74F4F] mt-1">{errors.password}</p>}
              </div>
            </div>
            <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
              <button onClick={onClose} className="neivai-btn-secondary">Cancel</button>
              <button
                data-testid="salesperson-save-btn"
                onClick={save}
                disabled={busy}
                className="neivai-btn-primary disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create salesperson"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResetPasswordDialog({ person, onClose }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post(`/salespersons/${person.id}/reset-password`, { new_password: password });
      toast.success(`Password updated for ${person.name}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C1E]/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h3 className="text-xl font-display font-medium">Reset Password</h3>
            <p className="text-sm text-[#8A8D91] mt-0.5">{person.name} · {person.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8D91] hover:text-[#1A1C1E]">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
          <button onClick={onClose} className="neivai-btn-secondary">Cancel</button>
          <button onClick={save} disabled={busy} className="neivai-btn-primary disabled:opacity-60">
            {busy ? "Saving…" : "Set Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddCreditsDialog({ person, onClose, onSaved }) {
  const [mode, setMode] = useState("add");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const credits = parseInt(amount, 10);
    if (!credits || credits <= 0) return toast.error("Enter a valid credit amount");
    setBusy(true);
    try {
      const endpoint = mode === "add"
        ? `/salespersons/${person.id}/add-credits`
        : `/salespersons/${person.id}/deduct-credits`;
      await api.post(endpoint, { credits });
      toast.success(mode === "add"
        ? `${credits} credits added to ${person.name}`
        : `${credits} credits deducted from ${person.name} and returned to your account`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update credits");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C1E]/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h3 className="text-xl font-display font-medium">Manage Credits</h3>
            <p className="text-sm text-[#8A8D91] mt-0.5">{person.name} · {person.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#F3F1EC]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex rounded-lg border border-[#E5E5E5] overflow-hidden">
            <button
              onClick={() => setMode("add")}
              className={`flex-1 py-2 text-sm font-medium transition ${mode === "add" ? "bg-[#C86B53] text-white" : "bg-white text-[#5C5F62] hover:bg-[#F3F1EC]"}`}
            >
              Add Credits
            </button>
            <button
              onClick={() => setMode("deduct")}
              className={`flex-1 py-2 text-sm font-medium transition ${mode === "deduct" ? "bg-[#C86B53] text-white" : "bg-white text-[#5C5F62] hover:bg-[#F3F1EC]"}`}
            >
              Deduct Credits
            </button>
          </div>
          <p className="text-sm text-[#5C5F62]">
            {mode === "add"
              ? "Credits will be deducted from your account and added to this salesperson."
              : "Credits will be deducted from this salesperson and returned to your account."}
          </p>
          <input
            type="number"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Number of credits"
            className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53]"
          />
        </div>
        <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
          <button onClick={onClose} className="neivai-btn-secondary">Cancel</button>
          <button onClick={save} disabled={busy} className="neivai-btn-primary disabled:opacity-60">
            {busy ? "Saving…" : mode === "add" ? "Add Credits" : "Deduct Credits"}
          </button>
        </div>
      </div>
    </div>
  );
}
